import { getAccessToken } from './token-store';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const STORAGE_KEY = 'as_session_source';

const readStored = () => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStored = (value) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Private-mode browsers reject writes; attribution is best-effort
  }
};

const clearStored = () => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // See writeStored
  }
};

const referrerSource = () => {
  const referrer = typeof document === 'undefined' ? '' : document.referrer;

  if (!referrer) return 'direct';

  try {
    const { hostname } = new URL(referrer);

    if (!hostname || hostname === window.location.hostname) return 'direct';

    return `referral_${hostname.replace(/^www\./, '')}`;
  } catch {
    return 'direct';
  }
};

/**
 * `email_daily_summary`, `google_cpc`, `referral_reddit.com` or `direct`.
 * The `<source>_<medium>` shape is what the API keys email engagement off.
 */
export const resolveSessionSource = (searchParams) => {
  const utmSource = searchParams?.get?.('utm_source');

  if (utmSource) return `${utmSource}_${searchParams.get('utm_medium') || 'unknown'}`;

  return referrerSource();
};

/**
 * Landing happens before the visitor has a token — often before they even have
 * an account — so the source is parked until there is someone to attach it to.
 */
export const captureSessionSource = (searchParams) => {
  const isCampaign = Boolean(searchParams?.get?.('utm_source'));

  // A campaign arrival says more than the direct/referral default it replaces;
  // otherwise the first touch of the session wins.
  if (readStored() && !isCampaign) return;

  writeStored(resolveSessionSource(searchParams));
};

export async function recordSessionSource(source) {
  const token = getAccessToken();

  if (!token || !source) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/analytics/session-source`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ source }),
    });

    return Boolean(response?.ok);
  } catch {
    return false;
  }
}

/** No-ops while signed out, so the pending source survives until login. */
export const flushSessionSource = async () => {
  const source = readStored();

  if (!source) return;

  if (await recordSessionSource(source)) clearStored();
};
